import { useEffect, useState } from "react";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { useChatSwUpdate, type ChatUpdatePhase } from "@/hooks/useChatSwUpdate";

/** Étapes d’attente (UI seule — n’affecte pas l’install SW). */
const WAIT_STEPS = [
  "Vérification de la version…",
  "Récupération des fichiers…",
  "Mise à jour du cache…",
  "Activation de l’application…",
  "Presque terminé…",
] as const;

const STEP_MS = 1_400;

function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

function WaitSteps({ phase }: { phase: ChatUpdatePhase }) {
  const [step, setStep] = useState(0);
  const done = phase === "reloading";
  const activeStep = done ? WAIT_STEPS.length - 1 : Math.min(step, WAIT_STEPS.length - 1);
  const progress = done
    ? 100
    : Math.min(92, Math.round(((activeStep + 1) / WAIT_STEPS.length) * 88));

  useEffect(() => {
    if (done) {
      setStep(WAIT_STEPS.length - 1);
      return;
    }
    setStep(0);
    const id = window.setInterval(() => {
      setStep((prev) => {
        if (prev >= WAIT_STEPS.length - 1) return prev;
        return prev + 1;
      });
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [done]);

  return (
    <div className="chat-update-overlay__stages">
      <div
        className="chat-update-overlay__progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        aria-label="Progression de la mise à jour"
      >
        <span
          className="chat-update-overlay__progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
      <ul className="chat-update-overlay__steps">
        {WAIT_STEPS.map((label, index) => {
          const complete = index < activeStep || (done && index <= activeStep);
          const current = !done && index === activeStep;
          return (
            <li
              key={label}
              className={cn(
                "chat-update-overlay__step",
                complete && "chat-update-overlay__step--done",
                current && "chat-update-overlay__step--current"
              )}
            >
              <span className="chat-update-overlay__step-mark" aria-hidden>
                {complete ? (
                  <Check className="chat-update-overlay__step-check" strokeWidth={2.5} />
                ) : current ? (
                  <Loader2 className="chat-update-overlay__step-spin" />
                ) : (
                  <span className="chat-update-overlay__step-dot" />
                )}
              </span>
              <span className="chat-update-overlay__step-label">{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Bannière MAJ — clic explicite, overlay pendant l'installation, reload annoncé. */
export function ChatUpdateBanner() {
  const { phase, updateReady, applying, applyUpdate, forceReload } = useChatSwUpdate();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!applying) {
      setStuck(false);
      return;
    }
    const t = window.setTimeout(() => setStuck(true), 12_000);
    return () => window.clearTimeout(t);
  }, [applying]);

  return (
    <>
      {applying ? (
        <div
          className="chat-update-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="chat-update-overlay-title"
          aria-describedby="chat-update-overlay-desc"
        >
          <div className="chat-update-overlay__card">
            <Loader2 className="chat-update-overlay__spinner" aria-hidden />
            <p id="chat-update-overlay-title" className="chat-update-overlay__title">
              Mise à jour en cours
            </p>
            <p id="chat-update-overlay-desc" className="chat-update-overlay__detail">
              Installation de la nouvelle version
            </p>
            <WaitSteps phase={phase} />
            <p className="chat-update-overlay__hint">
              Ne fermez pas cet onglet — l&apos;application va se recharger automatiquement.
            </p>
            {stuck ? (
              <button
                type="button"
                className="chat-update-banner__btn"
                style={{ marginTop: "0.85rem", width: "100%" }}
                onClick={() => forceReload()}
              >
                Forcer le rechargement
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {updateReady && !applying ? (
        <div className="chat-update-banner" role="status" aria-live="polite">
          <div className="chat-update-banner__inner">
            <span className="chat-update-banner__label">
              <RefreshCw className="chat-update-banner__icon" aria-hidden />
              <span className="chat-update-banner__label-text">Mise à jour disponible</span>
            </span>
            <button
              type="button"
              className="chat-update-banner__btn"
              onClick={() => void applyUpdate()}
            >
              Installer
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
